/**
 * User Badge List Component
 * Grid layout for displaying a user's earned badges
 */

import { BadgeCard } from './BadgeCard';
import { cn } from '../../lib/utils';
import type { BadgeDefinition, UserBadge } from '../../lib/types/badge';

interface UserBadgeListProps {
  userBadges: UserBadge[];
  badgeDefinitions: BadgeDefinition[];
  isLoading?: boolean;
  error?: string | null;
  className?: string;
  onBadgeClick?: (badgeId: string) => void; // For future details view
}

function BadgeListSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={`skeleton-${index}`}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          {/* Icon skeleton */}
          <div className="flex justify-center mb-3">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          </div>
          {/* Name skeleton */}
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
          {/* Date skeleton */}
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3 mx-auto" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <svg
        className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
        No badges unlocked yet
      </p>
    </div>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <div className="text-center py-12">
      <svg
        className="w-16 h-16 mx-auto text-red-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
        Unable to load badges
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
        {error}
      </p>
    </div>
  );
}

export function UserBadgeList({
  userBadges,
  badgeDefinitions,
  isLoading = false,
  error = null,
  className = '',
  onBadgeClick,
}: UserBadgeListProps) {
  // Show loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        <BadgeListSkeleton />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={className}>
        <ErrorState error={error} />
      </div>
    );
  }

  // Show empty state
  if (!userBadges.length) {
    return (
      <div className={className}>
        <EmptyState />
      </div>
    );
  }

  // Create a map for quick badge definition lookup
  const badgeDefinitionMap = new Map(
    badgeDefinitions.map(badge => [badge.badgeId, badge])
  );

  // Filter userBadges to only include those with valid definitions
  const validUserBadges = userBadges.filter(userBadge =>
    badgeDefinitionMap.has(userBadge.badgeId)
  );

  // Show badges grid
  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Your Badges
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {validUserBadges.length} badge
            {validUserBadges.length !== 1 ? 's' : ''} earned
          </p>
        </div>
      </div>

      {/* Badge Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {validUserBadges.map(userBadge => {
          const badgeDefinition = badgeDefinitionMap.get(userBadge.badgeId);

          if (!badgeDefinition) return null;

          return (
            <BadgeCard
              key={userBadge.badgeId}
              badge={badgeDefinition}
              userBadge={userBadge}
              onClick={
                onBadgeClick ? () => onBadgeClick(userBadge.badgeId) : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}
